import React, { memo, useEffect } from "react";
import { compose } from "redux";
import { connect, useDispatch } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
import {
  makeSelectConnectionDetailsForm,
  makeSelectEligiblePlans,
  makeSelectIsPortingUser,
  makeSelectPageValidations,
  makeSelectPlanDetails,
  makeSelectUserLineNumbers,
  makeSelectIsFetchingData,
  makeSelectIsPortingUserWithPortingNumber,
  makeSelectPortinExpiryDateOptions
} from "ducks/selectors";
import SmartRender from "containers/SmartRender";
import validate from "validations";
import { forms, CONTRACT_TYPES } from "utils/constants";
import { fetchUserKycDetails, selectPlan } from "ducks/actions";
import Spinner, { styles } from "components/Spinner";
import isFunction from "lodash/isFunction";
import isEmpty from "lodash/isEmpty";
import head from "lodash/head";
import { portinProviders, setNewRelicAttribute } from "utils/helpers";

const ConnectionDetailsHOC = Component =>
  function Comp(props) {
    const {
      isPortingUser,
      appSettings,
      isFetchingMNOKycData,
      isPortingUserWithPortingNumber,
      change,
      eligiblePlans,
      planDetails
    } = props;
    const dispatch = useDispatch();

    useEffect(
      () => {
        if (isEmpty(planDetails) && !isEmpty(eligiblePlans)) {
          const selectedPlan = head(eligiblePlans);
          !isEmpty(selectedPlan) && dispatch(selectPlan(selectedPlan));
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [eligiblePlans, planDetails]
    );

    useEffect(() => {
      if (isPortingUser) {
        const providers = portinProviders();
        const kycTypes = providers.map(provider => `${provider}_PORTIN`);

        const params = {
          kyc_provider: providers?.join(",") || "",
          kyc_type: kycTypes?.join(",") || ""
        };

        dispatch(
          fetchUserKycDetails(params, (result = {}) => {
            setNewRelicAttribute(
              "mno_kyc_first_name",
              result?.first_name,
              "rsa"
            );
            setNewRelicAttribute("mno_kyc_last_name", result?.last_name, "rsa");
            setNewRelicAttribute("mno_kyc_birthday", result?.birthday, "rsa");
          })
        );
      }
    }, [dispatch, isPortingUser, appSettings]);

    useEffect(() => {
      if (isPortingUserWithPortingNumber) {
        isFunction(change) && change("number_type", CONTRACT_TYPES.PORT_IN);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPortingUserWithPortingNumber]);

    return isPortingUser && isFetchingMNOKycData ? (
      <Spinner size={200} sx={styles.fullPage} />
    ) : (
      <Component {...props} />
    );
  };

const mapStateToProps = createStructuredSelector({
  formValues: makeSelectConnectionDetailsForm(),
  portInNumbers: makeSelectUserLineNumbers(),
  initialValues: _ => {
    return {
      sim_type: "physical-sim",
      number_type: CONTRACT_TYPES.PORT_IN,
      switch_active_line: "true",
      have_mnp_reservation: "false"
    };
  },
  formValidations: makeSelectPageValidations(forms.CONNECTION_DETAILS_FORM),
  isPortingUser: makeSelectIsPortingUser(),
  isPortingUserWithPortingNumber: makeSelectIsPortingUserWithPortingNumber(),
  isFetchingMNOKycData: makeSelectIsFetchingData("userKycDetails"),
  portinExpiryDateOptions: makeSelectPortinExpiryDateOptions(),
  eligiblePlans: makeSelectEligiblePlans(),
  planDetails: makeSelectPlanDetails()
});

const withConnect = connect(mapStateToProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.CONNECTION_DETAILS_FORM,
    destroyOnUnmount: false,
    validate
  }),
  memo
)(ConnectionDetailsHOC(SmartRender));
